// @ts-strict-ignore
import { ajaxFetch } from '~/helpers/api';
import { routes } from '~/helpers/routes';
import type { ReasonCodeGroup } from '~/flow/types';

import type {
  MissingRequiredItemInfo,
  MissingRequiredItemInfoObject,
  MultiItemData,
  OutcomePayloadInTags,
  SingleItemData,
  TagPayloadData,
} from '../types';
import type {
  ReasonCode,
  TagObjectInAction,
  Threshold,
  WorkflowProps,
} from '../../../../store/engine/types';
import { autoCreationResults, creatorHelperResultStatuses } from '../constants';
import { getOutcomeByName } from '~/dashboard/views/workflows/service/outcomes/outcomes';

export const createMissingOutcomes = ({
  missingRequiredItems,
}: {
  missingRequiredItems: MissingRequiredItemInfo;
}) => {
  const outcomesToBeCreated = missingRequiredItems.filter(issue => issue.type === 'outcome');
  return Promise.all(
    outcomesToBeCreated.map(async issue => {
      if (issue.status === autoCreationResults.CREATED) return issue;
      if (issue.type === 'outcome') {
        try {
          const outcomeName = issue.status === autoCreationResults.ARCHIVED
            ? `${(issue.data as SingleItemData).name} (2)` : (issue.data as SingleItemData).name;
          const data = await ajaxFetch(routes.settings.outcomes(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ outcome_data: { outcome: outcomeName } }),
          });

          if (data.result === creatorHelperResultStatuses.SUCCESS) {
            issue.status = autoCreationResults.CREATED;
            issue.result = data.outcomes.find(
              outcome => outcome.customer_outcome_id === data.outcome_id);
          }

          return issue;
        } catch (error) {
          return issue;
        }
      }

      return issue;
    }),
  );
};

export const mapOutcomesForTag = ({
  tagData,
  updatedOutcomes,
  workflowProps,
}: {
  tagData: TagObjectInAction;
  updatedOutcomes: MissingRequiredItemInfo;
  workflowProps: WorkflowProps;
}) => tagData.outcomes.map(
  outcome => {
    const fromUpdatedOutcomes = updatedOutcomes.find(
      updatedOutcome => outcome.outcome
        === (updatedOutcome.result as OutcomePayloadInTags).outcome);

    if (fromUpdatedOutcomes?.result) {
      return fromUpdatedOutcomes.result;
    }

    const fromPropsOutcome = getOutcomeByName(workflowProps.outcomes, outcome.outcome);

    if (fromPropsOutcome) {
      return {
        outcome: fromPropsOutcome.outcome,
        color: fromPropsOutcome.outcome_color,
        archived: fromPropsOutcome.archived,
        is_case_status: fromPropsOutcome.is_case_status,
        customer_outcome_id: fromPropsOutcome.customer_outcome_id,
      };
    }

    return null;
  }).filter(outcome => outcome);

export const updateTagWithOutcome = async ({
  customerTagId, payloadTagData, tagData, associatedTagTypes, updatedOutcomes, workflowProps,
}: {
  customerTagId: number;
  payloadTagData: TagPayloadData;
  tagData: TagObjectInAction;
  associatedTagTypes: string[];
  updatedOutcomes: MissingRequiredItemInfo;
  workflowProps: WorkflowProps;
}) => {
  const data = payloadTagData;
  data.customer_tag_id = customerTagId;
  data.outcomes = mapOutcomesForTag({
    tagData,
    updatedOutcomes,
    workflowProps,
  }) as OutcomePayloadInTags[];

  try {
    const result = await ajaxFetch(routes.settings.tags, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tag_data: data,
        tag_type_names: associatedTagTypes,
      }),
    });

    return result;
  } catch (error) {
    return error;
  }
};

async function insertReasonCodes({
  newRCGObject,
  originalReasonCodeGroup,
  reasonCodes,
  issue,
}: {
  newRCGObject: ReasonCodeGroup & {
    customer_reason_code_group_id: number;
    updated: boolean;
  };
  originalReasonCodeGroup: ReasonCodeGroup;
  reasonCodes: ReasonCode[];
  issue: MissingRequiredItemInfoObject;
}) {
  const newRetryPendingItems = [];
  await Promise.all(
    originalReasonCodeGroup.reasonCodes.map(async reasonCode => {
      const originalCodeObj = reasonCode as ReasonCode;
      const matchingCode = reasonCodes.find(newCode => newCode.code === originalCodeObj.code
        && newCode.service_internal_name === originalCodeObj.service_internal_name);

      if (!matchingCode) return reasonCode;
      if ((
        issue.retry
        && Array.isArray(issue.retry)
        && (issue.retry as ReasonCode[]).includes(matchingCode)
      ) || !issue.retry) {
        const updatePayload = {
          group_data: {
            group_id: newRCGObject.id,
            group_name: newRCGObject.name,
            reason_code: {
              code: matchingCode,
              action: 'ADD',
            },
          },
        };

        try {
          const rcgUpdateResponse = await ajaxFetch(routes.settings.reasonCodeGroups, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatePayload),
          });

          if (rcgUpdateResponse?.result !== creatorHelperResultStatuses.SUCCESS) {
            throw new Error(creatorHelperResultStatuses.ERROR);
          }
        } catch (error) {
          issue.status = autoCreationResults.CREATED_PENDING_REASONCODE_UPDATE;
          if (issue.retry?.length > 0) {
            newRetryPendingItems.push(matchingCode);
          } else {
            (issue.retry as ReasonCode[]).push(matchingCode);
          }
        }
      }

      return reasonCode;
    }),
  );

  (issue.retry as ReasonCode[]) = newRetryPendingItems;
  if (newRetryPendingItems.length > 0) {
    (issue.retry as ReasonCode[]) = newRetryPendingItems;
  }
  if (issue.status === autoCreationResults.CREATED_PENDING_REASONCODE_UPDATE) {
    issue.result = newRCGObject;
    return issue;
  }

  issue.status = autoCreationResults.CREATED;
  issue.result = newRCGObject.id;
  return issue;
}

async function createReasonCodeGroup({
  reasonCodes,
  issue,
}: {
  reasonCodes: ReasonCode[];
  issue: MissingRequiredItemInfoObject;
}) {
  try {
    const originalReasonCodeGroup = issue.data[0].originalReasonCodeGroup as ReasonCodeGroup; // all data originalReasonCodeGroups are same

    let newRCGObject;
    if (issue.status === autoCreationResults.CREATED_PENDING_REASONCODE_UPDATE) {
      newRCGObject = issue.result;
    } else {
      const creationResult = await ajaxFetch(routes.settings.reasonCodeGroups, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          group_data: {
            name: originalReasonCodeGroup.name,
          },
        }),
      });

      if (creationResult.reasonCodeGroups) {
        newRCGObject = creationResult.reasonCodeGroups.find(rcg => (
          rcg.name === originalReasonCodeGroup.name
        ));
      }
    }

    const reasonCodeInsertionResult = await insertReasonCodes({
      newRCGObject,
      originalReasonCodeGroup,
      reasonCodes,
      issue,
    });
    return reasonCodeInsertionResult;
  } catch (error) {
    return error;
  }
}

export const createOtherMissingItems = ({
  missingRequiredItems, workflowProps, updatedOutcomes,
}: {
  missingRequiredItems: MissingRequiredItemInfo;
  workflowProps: WorkflowProps;
  updatedOutcomes: MissingRequiredItemInfo;
}) => Promise.all(
  missingRequiredItems.map(async issue => {
    if (issue.status === autoCreationResults.CREATED) return issue;
    if (issue.type === 'tag') {
      const tagData: TagObjectInAction = issue.data as TagObjectInAction;
      const payloadTagData = {
        category_id: tagData.categoryId,
        is_case_creating: tagData.isCaseCreating,
        suppression_interval: tagData.suppressionInterval,
        tag_color: tagData.color,
        tag_name: issue.status === autoCreationResults.ARCHIVED ? `${tagData.name} (2)` : tagData.name,
      };
      let associatedTagTypes = [];
      if (tagData.isCaseCreating) {
        associatedTagTypes = tagData.associatedTypes;

        await Promise.all(
          associatedTagTypes.map(async tagType => {
            const tagTypeExists = workflowProps.tagTypes.find(
              existingTagType => existingTagType.name === tagType);
            if (!tagTypeExists) {
              try {
                const tagTypeResponse = await ajaxFetch(
                  routes.v3.settings.tagTypes,
                  {
                    method: 'POST',
                    body: JSON.stringify({ name: tagType }),
                    headers: {
                      'Content-Type': 'application/json',
                    },
                  });

                if (tagTypeResponse?.result !== creatorHelperResultStatuses.SUCCESS) {
                  issue.status = autoCreationResults.NOT_CREATED_TAGTYPE_PENDING_CREATION;
                }
              } catch (error) {
                issue.status = autoCreationResults.NOT_CREATED_TAGTYPE_PENDING_CREATION;
              }
            }

            return tagType;
          }),
        );
      }

      try {
        if (issue.status !== autoCreationResults.CREATED_PENDING_OUTCOME_UPDATE) {
          const data = await ajaxFetch(routes.settings.tags, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tag_data: payloadTagData,
              tag_type_names: associatedTagTypes,
            }),
          });

          if (data.result === creatorHelperResultStatuses.SUCCESS) {
            (issue.data as SingleItemData).customer_tag_id = data.customer_tag_id;
            issue.status = autoCreationResults.CREATED_PENDING_OUTCOME_UPDATE;
            if (tagData.outcomes?.length > 0) {
              const updateResult = await updateTagWithOutcome({
                customerTagId: data.customer_tag_id,
                payloadTagData,
                tagData,
                associatedTagTypes,
                updatedOutcomes,
                workflowProps,
              });

              if (updateResult?.result === creatorHelperResultStatuses.SUCCESS) {
                issue.status = autoCreationResults.CREATED;
              }
            } else {
              issue.status = autoCreationResults.CREATED;
            }
          }
        } else {
          const updateResult = await updateTagWithOutcome({
            customerTagId: (issue.data as SingleItemData).customer_tag_id as number,
            payloadTagData,
            tagData,
            associatedTagTypes,
            updatedOutcomes,
            workflowProps,
          });

          if (updateResult?.result === creatorHelperResultStatuses.SUCCESS) {
            issue.status = autoCreationResults.CREATED;
          }
        }

        return issue;
      } catch (error) {
        return issue;
      }
    }

    if (issue.type === 'reasoncodegroup') {
      const newReasonCodeGroupCreationResult = await createReasonCodeGroup({
        reasonCodes: workflowProps.reasonCodes,
        issue,
      });

      if (newReasonCodeGroupCreationResult.result) {
        return newReasonCodeGroupCreationResult;
      }
      return issue;
    }

    return issue;
  }),
);

export function remapThresholdReasonCodeGroupIds({
  thresholds,
  missingRequiredItems,
}: {
  thresholds: Record<string, Threshold>;
  missingRequiredItems: MissingRequiredItemInfo;
}) {
  const reasonCodeIssuesWithNewIds = missingRequiredItems.filter(issue => issue.type === 'reasoncodegroup'
    && issue.status === autoCreationResults.CREATED && issue.result);
  reasonCodeIssuesWithNewIds.forEach(issue => {
    (issue.data as MultiItemData).forEach(object => {
      const id = object.threshold as string;
      thresholds[id].right.reasonCodeGroupId = issue.result as number;
    });
  });

  return thresholds;
}
